'use client';

import { useEffect, useEffectEvent, useRef } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { hasUrlOrTaskQuestion } from '@/shared-question-model';
import {
  audioQueueAtom,
  markAudioCompletedAtom,
  useProjectResponseModeContext,
} from '@/shared-survey';
import {
  centralAudioElementAtom,
  currentPlayingIdAtom,
  stopAudioPlayback,
} from '@/shared-ui';
import { shouldStopAiOnUserAudio } from '@/shared-utilities';
import type { GetProjectQuery } from '@/survey-graphql';

import type { HandsFreeContentSession } from './project-usability-handsfree-content-session.hooks';
import { useHandsFreeActivityTracking } from './project-usability-handsfree-controls.hooks';
import { useHandsFreeFallbackUI } from './project-usability-handsfree-fallback-ui.hooks';
import { useHandsFreeListeningRecovery } from './project-usability-handsfree-listening-recovery.hooks';
import { toRecordingQuestionKind } from './project-usability-recording-question-kind.helpers';
import { useUsabilitySpeechDetection } from './project-usability-speech-detection.hooks';

interface HandsFreeContentVoiceOptions extends Pick<
  HandsFreeContentSession,
  | 'audioLevel'
  | 'isRecording'
  | 'isFinalizingRecording'
  | 'isSubmitting'
  | 'isConversationOver'
  | 'isAiVoiceEnabled'
  | 'isCurrentlyPaused'
  | 'effectivePaused'
  | 'updateActivity'
> {
  project: Pick<GetProjectQuery['project'], 'questions'>;
  isMobileMode: boolean;
}

export interface HandsFreeContentVoice {
  isSpeaking: boolean;
  hasPendingAudio: boolean;
  effectiveAudioLevel: number;
  isStuckListening: boolean;
  effectiveIsConversationOver: boolean;
  ttsHasDrained: boolean;
  hasUrlOrTask: boolean;
  forceStopAiPlayback: () => void;
  handleRestoreListening: () => void;
  showPrompt: boolean;
  isFadingOut: boolean;
  onSpeechPromptClose: () => void;
}

export function useHandsFreeContentVoice({
  project,
  isMobileMode,
  audioLevel,
  isRecording,
  isFinalizingRecording,
  isSubmitting,
  isConversationOver,
  isAiVoiceEnabled,
  isCurrentlyPaused,
  effectivePaused,
  updateActivity,
}: HandsFreeContentVoiceOptions): HandsFreeContentVoice {
  const sustainedSpeechStartRef = useRef<number | null>(null);

  const {
    responseMode,
    isListening,
    isStarting,
    startListening,
    stopListening,
  } = useProjectResponseModeContext();

  const audioQueue = useAtomValue(audioQueueAtom);
  const centralAudio = useAtomValue(centralAudioElementAtom);
  const currentPlayingId = useAtomValue(currentPlayingIdAtom);

  const markAudioCompleted = useSetAtom(markAudioCompletedAtom);

  const isSpeaking = currentPlayingId !== null;
  const hasPendingAudio = audioQueue.some(
    (i) =>
      i.status === 'pending' ||
      i.status === 'loading' ||
      i.status === 'ready' ||
      i.status === 'playing'
  );
  const effectiveAudioLevel =
    isSpeaking && audioLevel < 0.15 ? 0.35 : audioLevel;

  useHandsFreeListeningRecovery({
    isRecording: isMobileMode ? false : isRecording,
    isCurrentlyPaused: effectivePaused,
    isSubmitting,
    isConversationOver,
    isAiVoiceEnabled,
    isListening,
    isStarting,
    audioLevel: effectiveAudioLevel,
    startListening,
    stopListening,
  });

  const { isStuckListening, setIsStuckListening } = useHandsFreeFallbackUI({
    isRecording: isMobileMode ? false : isRecording,
    responseMode,
    isAiVoiceEnabled,
    isConversationOver,
    isCurrentlyPaused: effectivePaused,
    isSubmitting,
    isSpeaking,
    isListening,
    isStarting,
  });

  useHandsFreeActivityTracking({
    isRecording,
    isListening,
    isSpeaking,
    updateActivity,
  });

  const effectiveIsConversationOver =
    isConversationOver && !isFinalizingRecording;
  const ttsHasDrained =
    isConversationOver &&
    (!isAiVoiceEnabled || (!isSpeaking && !hasPendingAudio));
  const isRecordingActive = isRecording && !isCurrentlyPaused;
  const hasUrlOrTask = hasUrlOrTaskQuestion(
    project.questions.map((question) => ({
      kind: toRecordingQuestionKind(question.type),
      contentUrl: question.contentUrl,
      text: question.text,
    }))
  );

  const forceStopAiPlayback = () => {
    if (centralAudio && !centralAudio.paused) {
      centralAudio.pause();
    }
    stopAudioPlayback();
    audioQueue.forEach((item) => {
      if (item.status === 'playing' || item.status === 'ready') {
        markAudioCompleted(item.messageId);
      }
    });
  };

  const handleRestoreListening = () => {
    setIsStuckListening(false);
    stopListening(true)
      .then(() => startListening())
      .catch((error: unknown) => {
        console.error('[hands-free] restore listening failed', error);
      });
  };

  const handleAudioLevelChange = useEffectEvent((nextAudioLevel: number) => {
    const { shouldStop, nextSustainedStartedAt } = shouldStopAiOnUserAudio({
      isAiVoiceEnabled,
      isSpeaking,
      audioLevel: nextAudioLevel,
      sustainedStartedAt: sustainedSpeechStartRef.current,
      now: Date.now(),
    });
    sustainedSpeechStartRef.current = nextSustainedStartedAt;
    if (shouldStop) {
      forceStopAiPlayback();
    }
  });

  useEffect(() => {
    handleAudioLevelChange(audioLevel);
  }, [audioLevel]);

  const {
    showPrompt,
    isFadingOut,
    onClose: onSpeechPromptClose,
  } = useUsabilitySpeechDetection({
    isRecording: isRecordingActive,
    isSubmitting,
  });

  return {
    isSpeaking,
    hasPendingAudio,
    effectiveAudioLevel,
    isStuckListening,
    effectiveIsConversationOver,
    ttsHasDrained,
    hasUrlOrTask,
    forceStopAiPlayback,
    handleRestoreListening,
    showPrompt,
    isFadingOut,
    onSpeechPromptClose,
  };
}
